// Feature: address-geolocation
//
// Address — structured postal address (never used to identify a physical
// location; that is `GeoLocation`'s job).
//
// Design ref: `.kiro/specs/address-geolocation/design.md`. Requirements 2.1,
// 2.2, 2.3, 2.4, 2.5.
//
// Adopted additively on Building, Contact, Unit. Legacy free-text `address`
// strings on those models continue to work — this model does not replace
// them, it augments them (Req 11.1, 11.2).

/**
 * Structured postal address. Every field is trimmed / plain UTF-8 text.
 *
 * Required fields per Req 2.1: `addressLine1`, `town`, `postcode`,
 * `countryCode`. All other fields are optional.
 *
 * `countryCode` is ISO 3166-1 alpha-2 (e.g. `"GB"`). Callers should not embed
 * the country name in a free-text line — the country goes in `countryCode`.
 */
export interface Address {
  /** Organisation name (Company/Ltd/etc.) — not personal name. Optional. */
  organisation?: string;
  addressLine1: string;
  addressLine2?: string;
  /** Locality / neighbourhood (e.g. district within a town). Optional. */
  locality?: string;
  town: string;
  /** County / region / state. Optional. */
  county?: string;
  postcode: string;
  /** ISO 3166-1 alpha-2 country code (e.g. `"GB"`, `"IE"`, `"FR"`). */
  countryCode: string;
}

/**
 * Format a structured Address as a single-line `Formatted_Address` string.
 *
 * Pure, deterministic, side-effect free. Trims each field, joins non-empty
 * fields with `", "`, appends the country code last. Never mutates the input.
 * Repeated invocations on the same input MUST produce the same output
 * (Property 2 in the spec).
 *
 * The formatted string is a **derived** view — never store it as the
 * canonical source of truth (Req 2.3, 2.4). Callers regenerate on demand.
 */
export function formatAddress(address: Address): string {
  const parts: string[] = [];

  const push = (v: string | undefined): void => {
    if (typeof v !== "string") return;
    const trimmed = v.trim();
    if (trimmed.length > 0) parts.push(trimmed);
  };

  push(address.organisation);
  push(address.addressLine1);
  push(address.addressLine2);
  push(address.locality);
  push(address.town);
  push(address.county);
  push(address.postcode);
  push(address.countryCode);

  return parts.join(", ");
}
